import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { Editor } from "@tiptap/core";
import {
  Document,
  resolveDocumentHref,
} from "../../../../../store/reducers/builderReducers/documents/documentsApi";
import { useAppSelector } from "../../../../../store/hooks";
import { updateStructureComponent } from "../../../../../store/reducers/builderReducers/structure/structureActions";
import DocumentLinkPicker from "./DocumentLinkPicker/DocumentLinkPicker";
import {
  applyFileLinkAtRange,
  type LinkRange,
} from "./fileLinkEditor";
import {
  clearPendingFileLinkSelection,
  rememberEditorTextSelection,
} from "./fileLinkSelectionStore";

type FileLinkPickerContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
  savePendingLinkRange: (editor: Editor) => void;
  getPendingLinkRange: () => LinkRange | null;
};

const FileLinkPickerContext = createContext<FileLinkPickerContextValue | null>(
  null
);

export function FileLinkPickerProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const pendingLinkRangeRef = useRef<LinkRange | null>(null);
  const editor = useAppSelector((state) => state.builder.editor.editor);
  const editableComponentId = useAppSelector(
    (state) => state.textEditor?.get?.("id") ?? state.textEditor?.id ?? null
  );

  useEffect(() => {
    if (!editor) {
      return;
    }

    const trackSelection = () => {
      rememberEditorTextSelection(editor);
    };

    trackSelection();
    editor.on("selectionUpdate", trackSelection);

    return () => {
      editor.off("selectionUpdate", trackSelection);
    };
  }, [editor]);

  const savePendingLinkRange = useCallback((activeEditor: Editor) => {
    const remembered = rememberEditorTextSelection(activeEditor);
    if (remembered) {
      pendingLinkRangeRef.current = {
        from: remembered.from,
        to: remembered.to,
      };
    }
  }, []);

  const getPendingLinkRange = useCallback(() => pendingLinkRangeRef.current, []);

  const handleSelect = useCallback(
    (doc: Document) => {
      if (!editor) {
        return;
      }

      const href = resolveDocumentHref(doc);
      if (!href) {
        return;
      }

      const applied = applyFileLinkAtRange(
        editor,
        href,
        pendingLinkRangeRef.current
      );

      if (!applied) {
        return;
      }

      if (editableComponentId != null) {
        updateStructureComponent(
          editableComponentId,
          "attributes.text",
          editor.getHTML(),
          true
        );
      }

      pendingLinkRangeRef.current = null;
      clearPendingFileLinkSelection();
      setOpen(false);
    },
    [editor, editableComponentId]
  );

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const value = useMemo(
    () => ({
      open,
      setOpen,
      savePendingLinkRange,
      getPendingLinkRange,
    }),
    [open, savePendingLinkRange, getPendingLinkRange]
  );

  return (
    <FileLinkPickerContext.Provider value={value}>
      {children}
      <DocumentLinkPicker
        open={open}
        onClose={handleClose}
        onSelect={handleSelect}
      />
    </FileLinkPickerContext.Provider>
  );
}

export function useFileLinkPicker() {
  const context = useContext(FileLinkPickerContext);
  if (!context) {
    throw new Error("useFileLinkPicker must be used within FileLinkPickerProvider");
  }
  return context;
}
